import {useEffect, useRef, useState} from 'react';
import type {StagedFile} from '@securspace/securspace-ui-kit';
import type {ImagePreviewAction} from './ImagePreviewModal';

const ACTIONS_WARNING =
  'ImagePreviewModal: `actions` prop changed identity. Wrap in useMemo to prevent unnecessary re-renders.';

export interface UseImagePreviewActionResult {
  /** The currently active action, or `null` when in plain preview mode. */
  activeAction: ImagePreviewAction | null;
  /** Switches to the given action's panel. */
  selectAction: (actionId: string) => void;
  /** Applies the action's result and returns to preview mode. */
  handleComplete: (updated: StagedFile) => void;
  /** Discards the action and returns to preview mode. */
  handleCancel: () => void;
}

/**
 * Owns which `ImagePreviewAction` (if any) is active for `ImagePreviewModal`,
 * and warns in development if the `actions` array identity is unstable
 * (a common cause of unnecessary re-renders / effect churn for callers).
 */
export const useImagePreviewAction = (
  actions: ImagePreviewAction[],
  onActionComplete: (updated: StagedFile) => void,
  initialActionId: string | null = null
): UseImagePreviewActionResult => {
  const [activeActionId, setActiveActionId] = useState<string | null>(initialActionId);
  const activeAction = actions.find((action) => action.id === activeActionId) ?? null;
  const previousActionsRef = useRef(actions);

  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      previousActionsRef.current !== actions
    ) {
      console.warn(ACTIONS_WARNING);
    }
    previousActionsRef.current = actions;
  }, [actions]);

  const handleComplete = (updated: StagedFile) => {
    setActiveActionId(null);
    onActionComplete(updated);
  };

  const handleCancel = () => {
    setActiveActionId(null);
  };

  return {
    activeAction,
    selectAction: setActiveActionId,
    handleComplete,
    handleCancel,
  };
};

